import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	inject,
	OnInit,
	signal,
	WritableSignal,
} from '@angular/core';
import { BusinessFeatureFlagSelectors } from '@state/business';
import { Store } from '@ngxs/store';
import { UntilDestroy } from '@ngneat/until-destroy';
import { toSignal } from '@angular/core/rxjs-interop';
import {
	CollapsableBusinessSettingsSection,
	CollapsedChangeData,
	SectionCollapsedStates,
} from './business-finances-general.types';
import { TestHooks } from './business-finances-general.hooks';
import { ActivatedRoute } from '@angular/router';

@UntilDestroy()
@Component({
	selector: 'app-business-finances-general',
	templateUrl: './business-finances-general.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class BusinessFinancesGeneralComponent implements OnInit {
	testHooks = TestHooks;
	public CollapsableBusinessSettingsSection =
		CollapsableBusinessSettingsSection;

	public eSigningFlagSignal = toSignal(
		inject(Store).select(BusinessFeatureFlagSelectors.sc16163EnableSignIt),
	);

	public sectionCollapsedStates: WritableSignal<SectionCollapsedStates>;
	private activatedRoute = inject(ActivatedRoute);
	private cdr = inject(ChangeDetectorRef);

	constructor() {
		// All sections start in the collapsed state
		const initialSectionCollapsedStates = Object.keys(
			CollapsableBusinessSettingsSection,
		).reduce((acc, section) => {
			acc[section] = true;
			return acc;
		}, {} as SectionCollapsedStates);

		this.sectionCollapsedStates = signal<SectionCollapsedStates>(
			initialSectionCollapsedStates,
		);
	}

	ngOnInit(): void {
		const fragment = this.activatedRoute.snapshot.fragment;

		if (!fragment) return;
		if (fragment in this.sectionCollapsedStates()) {
			this.sectionCollapsedStates.update((states) => ({
				...states,
				[fragment as keyof SectionCollapsedStates]: false,
			}));
		}

		this.cdr.detectChanges();

		const el = document.getElementById(fragment);
		if (el) {
			el.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	}

	handleSectionCollapsedChange(sectionCollapsedEvent: CollapsedChangeData) {
		const { sectionName, collapsed } = sectionCollapsedEvent;
		const clonedCollapsedStates = structuredClone(
			this.sectionCollapsedStates(),
		);

		const sectionEnumKey = Object.entries(CollapsableBusinessSettingsSection)
			.find((kvp) => {
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				const [key, value] = kvp;
				return value === sectionName;
			})
			?.at(0);

		if (collapsed === false) {
			// When a section is expanded (not collapsed), all other section should be closed
			Object.keys(clonedCollapsedStates).forEach((sectionKey) => {
				if (sectionKey !== sectionEnumKey) {
					clonedCollapsedStates[sectionKey] = true;
				} else {
					clonedCollapsedStates[sectionKey] = false;
				}
			});
		} else {
			// When a section is collapsed, update the state of that section only
			clonedCollapsedStates[sectionEnumKey] = true;
		}

		this.sectionCollapsedStates.set(clonedCollapsedStates);
	}
}
